import { useTranslation } from 'react-i18next';
import { FC, ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CollapsibleSection, PageLarge, SubPage } from '@builder';
import { COMMON_TRANSLATION, HELIX_SUPPORT_EMAIL, PATHS } from '@common/constants';
import { CreateReferralResponse } from './CreatePatientReferralScreen.types';
import { EntryForm, InputType, SectionName } from '../../components/shared/ManualForm/ManualForm.types';
import { FindPatientIdError, PatientProfileResponse } from '../../types';
import { getDateWithoutTZ, maskPhoneNumber } from '@/common/utils';
import { GENETIC_COUNSELING_TRANSLATION } from '../../constants';
import { GroupIcon, StyledSectionTitle } from './CreatePatientReferralScreen.styles';
import { Notification, NotificationAlignment, NotificationVariants } from '@base-app';

import { BackButtonProps } from '@/common/components/builder/organisms/SubPage/SubPage.types';
import { useMutationCreateReferral, useQueryFindPatientId } from '../../hooks';
import { useTab } from '@/common/components/hooks';
import { useAddNotification } from '@/modules/notifications/store';

import FindPatientId from '../../components/FindPatientId/FindPatientId';
import ManualForm from '../../components/shared/ManualForm/ManualForm';

const CreatePatientReferralScreen: FC = () => {
  const { t } = useTranslation(GENETIC_COUNSELING_TRANSLATION);
  const { selectedTab } = useTab();

  const addNotification = useAddNotification();
  const navigate = useNavigate();

  const [errorDescription, setErrorDescription] = useState<string | undefined>();
  const [errorTitle, setErrorTitle] = useState<string | undefined>();
  const [formData, setFormData] = useState<EntryForm[]>();
  const [isSearching, setIsSearching] = useState<boolean>();
  const [patient, setPatient] = useState<PatientProfileResponse>();
  const [patientId, setPatientId] = useState<string>();

  const onSuccess = (data: CreateReferralResponse): void => {
    addNotification({
      autoClose: false,
      title: t('create.success'),
      type: NotificationVariants.SUCCESS,
    });

    navigate(`/${PATHS.GENETIC_COUNSELING.ROOT}/${PATHS.GENETIC_COUNSELING.PATIENT}/${data.appointmentID}`);
  };

  const onError = (): void => {
    addNotification({
      autoClose: true,
      title: t('create.error_title'),
      description: t('create.error_description', { email: HELIX_SUPPORT_EMAIL }),
      type: NotificationVariants.ERROR,
    });
  };

  const { isLoading, mutateAsync } = useMutationCreateReferral(onSuccess, onError);

  const renderTitle = (): ReactNode => {
    return (
      <StyledSectionTitle>
        <GroupIcon />
        {t('patient_profile.section_profile')}
      </StyledSectionTitle>
    );
  };

  const handleFindPatientIdSettled = (): void => {
    setIsSearching(false);
    setPatientId(undefined);
  };

  const handleFindPatientIdSuccess = (data: PatientProfileResponse | undefined): void => {
    setPatient(data);
  };

  const handleFindPatientIdError = (error: unknown): void => {
    const errorMsg = (error as Error)?.message || '';
    const errorTitle =
      errorMsg === FindPatientIdError.NO_MATCHING_ID
        ? t('create.find_patient_id.error.title')
        : t('errors.generic', { ns: COMMON_TRANSLATION });
    const errorDescription =
      errorMsg === FindPatientIdError.NO_MATCHING_ID ? t('create.find_patient_id.error.description') : undefined;
    setErrorTitle(errorTitle);
    setErrorDescription(errorDescription);
  };

  useQueryFindPatientId(
    {
      onSettled: handleFindPatientIdSettled,
      onSuccess: handleFindPatientIdSuccess,
      onError: handleFindPatientIdError,
      patientId,
    },
    {
      enabled: patientId !== undefined,
    },
  );

  const handleOnSubmit = async (patientId: string): Promise<void> => {
    setErrorTitle(undefined);
    setErrorDescription(undefined);
    setIsSearching(true);

    setPatientId(patientId);
  };

  const handleOnSubmitChange = async (values: any): Promise<void> => {
    void mutateAsync({ userID: patient?.userID, email: values[0].target, phone: values[1].target });
  };

  const getBackButton = (): BackButtonProps => {
    return {
      label:
        selectedTab === ''
          ? t('patient_profile.back')
          : t('patient_profile.back_to', { page: t(`${selectedTab}.tab`) }),
      url: `/${PATHS.GENETIC_COUNSELING.ROOT}/${selectedTab}`,
    };
  };

  useEffect(() => {
    if (patient) {
      setFormData([
        {
          name: t('patient_profile.name'),
          required: true,
          section: SectionName.DETAILS,
          target: patient.patientProfile.firstName,
          type: InputType.TEXT,
          isSensitive: true,
        },
        {
          name: t('patient_profile.last_name'),
          required: true,
          section: SectionName.DETAILS,
          target: patient.patientProfile.lastName,
          type: InputType.TEXT,
          isSensitive: true,
        },
        {
          name: t('patient_profile.patient_id'),
          required: true,
          section: SectionName.DETAILS,
          target: patient.patientProfile.patientID,
          type: InputType.TEXT,
          isSensitive: true,
        },
        {
          name: t('patient_profile.order_id'),
          required: true,
          section: SectionName.DETAILS,
          target: patient.orderID,
          type: InputType.TEXT,
        },
        {
          name: t('patient_profile.date_of_birth'),
          required: true,
          section: SectionName.DETAILS,
          target: getDateWithoutTZ(patient.patientProfile.dateOfBirth, 'MM/dd/yyyy'),
          type: InputType.TEXT,
          isSensitive: true,
        },
        {
          name: t('patient_profile.sex_at_birth'),
          required: true,
          section: SectionName.DETAILS,
          target: patient.patientProfile.sexAtBirth,
          type: InputType.TEXT,
          isSensitive: true,
        },
        {
          name: t('patient_profile.email'),
          required: false,
          section: SectionName.INFO,
          target: patient.patientProfile.email,
          type: InputType.EMAIL,
          isSensitive: true,
        },
        {
          name: t('patient_profile.phone_number'),
          required: true,
          section: SectionName.INFO,
          target: maskPhoneNumber(patient.patientProfile.phoneNumber),
          type: InputType.PHONE,
          isSensitive: true,
        },
        {
          name: t('patient_profile.service'),
          options: [
            {
              id: patient.serviceDetails.serviceID,
              label: patient.serviceDetails.service,
            },
          ],
          required: true,
          section: SectionName.SERVICE,
          target: patient.serviceDetails.serviceID,
          type: InputType.SELECT,
        },
        {
          name: t('patient_profile.specialty'),
          options: [
            {
              id: patient.serviceDetails.specialtyID,
              label: patient.serviceDetails.specialty,
            },
          ],
          required: true,
          section: SectionName.SERVICE,
          target: patient.serviceDetails.specialtyID,
          type: InputType.SELECT,
        },
        {
          name: t('patient_profile.sub_specialty'),
          options: [
            {
              id: patient.serviceDetails.subSpecialtyID,
              label: patient.serviceDetails.subSpecialty,
            },
          ],
          required: true,
          section: SectionName.SERVICE,
          target: patient.serviceDetails.subSpecialtyID,
          type: InputType.SELECT,
        },
      ]);
    }
  }, [patient]);

  return (
    <PageLarge>
      <SubPage backButton={getBackButton()} title={t('create.title')}>
        <>
          {errorTitle && (
            <Notification
              css={{ marginBottom: '$4' }}
              description={errorDescription}
              title={errorTitle}
              variant={NotificationVariants.ERROR}
              textAlign={NotificationAlignment.LEFT}
            />
          )}
          {!patient && <FindPatientId onSubmit={handleOnSubmit} isSearching={isSearching} />}
          {patient && formData && (
            <CollapsibleSection title={renderTitle()}>
              <ManualForm data={formData} hasClean={true} isLoading={isLoading} onSubmit={handleOnSubmitChange} />
            </CollapsibleSection>
          )}
        </>
      </SubPage>
    </PageLarge>
  );
};

export default CreatePatientReferralScreen;
